import { useCallback, useEffect, useRef, useState } from "react";
import { useAlertContext } from "../../context/AlertContext";
import { formatDateRu } from "../../utils/dateFormat";
import {
    deleteEmployeePhoto,
    getEmployeePhotoFile,
    getEmployeePhotos,
    updateEmployeePhoto,
    uploadEmployeePhoto,
} from "../../services/assetsApi";

/**
 * Вкладка «Фото» карточки сотрудника: несколько снимков с подписью и датой.
 * Это материал для будущих поздравлений, а не аватар — в шапке и в таблице фото не показываются.
 * Чат 4, правка №15 (MR 3), №21. [Claude] 2026-11-02
 */
const inputCls = "rounded-lg border border-slate-200 px-3 py-2 text-sm";

function PhotoThumb({ employeeId, photo }) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        let alive = true;
        let objectUrl = null;
        getEmployeePhotoFile(employeeId, photo.id)
            .then((blob) => {
                if (!alive || !blob) return;
                objectUrl = window.URL?.createObjectURL ? window.URL.createObjectURL(blob) : null;
                setUrl(objectUrl);
            })
            .catch(() => {});
        return () => {
            alive = false;
            if (objectUrl && window.URL?.revokeObjectURL) window.URL.revokeObjectURL(objectUrl);
        };
    }, [employeeId, photo.id]);
    return url
        ? <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={photo.caption || "Фото сотрудника"} className="h-40 w-full rounded-lg object-cover" /></a>
        : <div className="flex h-40 w-full items-center justify-center rounded-lg bg-slate-100 text-xs text-slate-400">{photo.filename}</div>;
}

export default function EmployeePhotosTab({ employeeId, canManage = false }) {
    const { showAlert } = useAlertContext();
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [caption, setCaption] = useState("");
    const [takenOn, setTakenOn] = useState("");
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await getEmployeePhotos(employeeId);
            setPhotos(Array.isArray(rows) ? rows : []);
        } catch (error) {
            showAlert(error.message || "Не удалось загрузить фото", "error");
        } finally {
            setLoading(false);
        }
    }, [employeeId, showAlert]);

    useEffect(() => { load(); }, [load]);

    const upload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;
        setUploading(true);
        let done = 0;
        for (const file of files) {
            try {
                await uploadEmployeePhoto(employeeId, file, { caption: caption.trim(), takenOn });
                done += 1;
            } catch (error) {
                showAlert(`${file.name}: ${error.message || "не загружено"}`, "error");
            }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
        if (done) {
            showAlert(done === 1 ? "Фото загружено" : `Загружено фото: ${done}`, "success");
            setCaption("");
            setTakenOn("");
            load();
        }
    };

    const saveEdit = async () => {
        try {
            const row = await updateEmployeePhoto(employeeId, editing.id, { caption: editing.caption || "", taken_on: editing.taken_on || null });
            setPhotos((list) => list.map((item) => (item.id === row.id ? row : item)));
            setEditing(null);
        } catch (error) {
            showAlert(error.message || "Не удалось сохранить", "error");
        }
    };

    const remove = async (photo) => {
        if (confirmDelete !== photo.id) {
            setConfirmDelete(photo.id);
            return;
        }
        try {
            await deleteEmployeePhoto(employeeId, photo.id);
            setPhotos((list) => list.filter((item) => item.id !== photo.id));
        } catch (error) {
            showAlert(error.message || "Не удалось удалить фото", "error");
        } finally {
            setConfirmDelete(null);
        }
    };

    return <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5" data-testid="employee-photos">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-bold text-slate-900">Фото</h2>
            <span className="text-xs text-slate-400">Для поздравлений. Видят те, кому дано право «Фото сотрудников».</span>
        </div>
        {canManage ? <div className="mb-4 flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3">
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Подпись (необязательно)</span>
                <input type="text" maxLength={300} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Корпоратив 2026" className={inputCls} data-testid="employee-photo-caption" /></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Дата снимка</span>
                <input type="date" value={takenOn} onChange={(e) => setTakenOn(e.target.value)} className={inputCls} data-testid="employee-photo-date" /></label>
            <label className={`cursor-pointer rounded-lg px-4 py-2 text-sm font-bold ${uploading ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-700"}`}>
                {uploading ? "Загрузка…" : "Добавить фото"}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple disabled={uploading} onChange={upload} className="hidden" data-testid="employee-photo-input" />
            </label>
            <span className="text-xs text-slate-400">JPG, PNG, WEBP, HEIC до 15 МБ, можно несколько сразу</span>
        </div> : null}
        {loading ? <p className="py-6 text-center text-sm text-slate-400">Загрузка…</p>
            : !photos.length ? <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="employee-photos-empty">Фото пока нет</p>
                : <ul className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="employee-photos-list">
                    {photos.map((photo) => <li key={photo.id} className="rounded-xl border border-slate-200 p-2" data-testid={`employee-photo-${photo.id}`}>
                        <PhotoThumb employeeId={employeeId} photo={photo} />
                        {editing?.id === photo.id ? <div className="mt-2 space-y-1">
                            <input type="text" maxLength={300} value={editing.caption || ""} onChange={(e) => setEditing((v) => ({ ...v, caption: e.target.value }))} className={`${inputCls} w-full`} />
                            <input type="date" value={editing.taken_on || ""} onChange={(e) => setEditing((v) => ({ ...v, taken_on: e.target.value }))} className={`${inputCls} w-full`} />
                            <div className="flex gap-2 text-xs">
                                <button type="button" onClick={saveEdit} className="font-bold text-emerald-700">Сохранить</button>
                                <button type="button" onClick={() => setEditing(null)} className="text-slate-500">Отмена</button>
                            </div>
                        </div> : <div className="mt-2 text-xs">
                            <div className="truncate font-semibold text-slate-700">{photo.caption || "Без подписи"}</div>
                            <div className="text-slate-400">{photo.taken_on ? formatDateRu(photo.taken_on) : "Дата не указана"}</div>
                            {canManage ? <div className="mt-1 flex gap-3">
                                <button type="button" onClick={() => setEditing({ id: photo.id, caption: photo.caption || "", taken_on: photo.taken_on || "" })} className="text-slate-500 hover:text-slate-800">Изменить</button>
                                <button type="button" onClick={() => remove(photo)} className="text-red-600 hover:text-red-800" data-testid={`delete-employee-photo-${photo.id}`}>{confirmDelete === photo.id ? "Точно удалить?" : "Удалить"}</button>
                            </div> : null}
                        </div>}
                    </li>)}
                </ul>}
    </section>;
}
